import { type UnifiedJobSearchResult } from '@kalos/kalos-rpc';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@kalos/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Fragment } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import { type FilterDimension } from '../../../../components/FilterSheet';
import { ListResultsPhone } from '../../../../components/ListPhone';
import { currentSearchHref } from '../../../../components/searchReturnTo';
import { useBatchUserQuery } from '../../../../hooks/react-query/useUserQuery';
import { formatDate } from '../../../../tools/helpers';
import { techniciansUserFilter } from '../../Pickers/newPickers/utils';
import { type ListSort } from '../listState';
import { PhoneSearchLayout } from '../PhoneSearchLayout';
import { jobStatusTone } from '../rowTones';
import { RowPill, SearchResultRow } from '../SearchResultRow';
import { JOB_SEARCH_SECTIONS } from './jobSearchSections';
import { UnifiedJobSearchForm } from './UnifiedJobSearchForm';
import { useUnifiedJobSearchPagesQuery } from './useUnifiedJobSearchQuery';
import {
  defaultUnifiedJobSearchValues,
  getResultRoute,
  isResultProject,
  type UnifiedJobSearchScheme,
} from './utils';

/** The table's sortable columns, each way. Date Started, newest first, is the default. */
const sortOptions = [
  { value: 'date_started.desc', label: 'Date Started, newest first' },
  { value: 'date_started.asc', label: 'Date Started, oldest first' },
  { value: 'log_jobNumber.desc', label: 'Job # / PO, highest first' },
  { value: 'log_jobNumber.asc', label: 'Job # / PO, lowest first' },
  { value: 'job_status.asc', label: 'Job Status, A to Z' },
  { value: 'job_status.desc', label: 'Job Status, Z to A' },
  { value: 'department.asc', label: 'Department, A to Z' },
  { value: 'department.desc', label: 'Department, Z to A' },
];

const formatFilterDate = (value: Date | undefined) =>
  value ? formatDate(value.toISOString()) : '';

type Lookups = {
  departmentName: (id: number) => string | undefined;
  jobTypeName: (id: number) => string | undefined;
  jobSubTypeName: (id: number) => string | undefined;
};

/**
 * Job search on phones: one box (the global search), every other filter under Filter in panels,
 * and one row per job that loads more as it scrolls. In a picker the row selects the job; on
 * the page it opens it. Customer, property, cost report and a new tab are the row's More actions.
 */
export const UnifiedJobSearchPhone = ({
  form,
  filters,
  sort,
  onSortChange,
  orderParams,
  lookups,
  canViewCostReport,
  onSelectResult,
}: {
  form: UseFormReturn<UnifiedJobSearchScheme>;
  /** The filters after the form's debounce, as the desktop table's request uses them. */
  filters: UnifiedJobSearchScheme;
  sort: ListSort | null;
  onSortChange: (sort: ListSort | null) => void;
  /** How the order is sent: the same mapping as the table. */
  orderParams: { orderBy: string; orderDir: 'ASC' | 'DESC' };
  lookups: Lookups;
  canViewCostReport?: boolean;
  /** Picker mode: the row selects the job. */
  onSelectResult?: (result: UnifiedJobSearchResult) => void;
}) => {
  const navigate = useNavigate();
  const pages = useUnifiedJobSearchPagesQuery({ filters, ...orderParams });

  const techniciansQuery = useBatchUserQuery({
    filters: techniciansUserFilter,
    select: (data) =>
      new Map(data.results.map((tech) => [String(tech.id), `${tech.firstname} ${tech.lastname}`])),
  });

  const text = (value: string | undefined) => value?.trim() ?? '';
  const dimensions: FilterDimension<UnifiedJobSearchScheme>[] = [
    {
      id: 'technicianAssigned',
      label: 'Technician Assigned',
      fields: ['technicianAssigned'],
      summary: (v) =>
        techniciansQuery.data?.get(v.technicianAssigned ?? '') ?? text(v.technicianAssigned),
    },
    {
      id: 'isResidential',
      label: 'Sector',
      fields: ['isResidential'],
      summary: (v) =>
        v.isResidential === 1 ? 'Residential' : v.isResidential === 2 ? 'Commercial' : '',
    },
    {
      id: 'departmentId',
      label: 'Department',
      fields: ['departmentId'],
      summary: (v) => (v.departmentId ? lookups.departmentName(v.departmentId) ?? '' : ''),
    },
    {
      id: 'jobTypeId',
      label: 'Type',
      fields: ['jobTypeId'],
      summary: (v) => (v.jobTypeId ? lookups.jobTypeName(v.jobTypeId) ?? '' : ''),
    },
    {
      id: 'jobSubtypeId',
      label: 'Subtype',
      fields: ['jobSubtypeId'],
      summary: (v) => (v.jobSubtypeId ? lookups.jobSubTypeName(v.jobSubtypeId) ?? '' : ''),
    },
    {
      id: 'jobStatus',
      label: 'Status',
      fields: ['jobStatus'],
      summary: (v) => {
        const statuses = text(v.jobStatus).split(',').filter(Boolean);
        return statuses.length > 1 ? `${statuses.length} selected` : statuses[0] ?? '';
      },
    },
    { id: 'projectsOnly', label: 'Projects Only', fields: ['projectsOnly'] },
    { id: 'serviceCallsOnly', label: 'Service Calls Only', fields: ['serviceCallsOnly'] },
    {
      id: 'dateStarted',
      label: 'Date Started',
      fields: ['dateStartedFrom', 'dateStartedTo'],
      summary: (v) =>
        [formatFilterDate(v.dateStartedFrom), formatFilterDate(v.dateStartedTo)].join(' to '),
    },
    {
      id: 'dateEnded',
      label: 'Date Ended',
      fields: ['dateEndedFrom', 'dateEndedTo'],
      summary: (v) =>
        [formatFilterDate(v.dateEndedFrom), formatFilterDate(v.dateEndedTo)].join(' to '),
    },
    {
      id: 'firstName',
      label: 'First Name',
      fields: ['firstName'],
      summary: (v) => text(v.firstName),
    },
    { id: 'lastName', label: 'Last Name', fields: ['lastName'], summary: (v) => text(v.lastName) },
    {
      id: 'businessName',
      label: 'Business Name',
      fields: ['businessName'],
      summary: (v) => text(v.businessName),
    },
    {
      id: 'propertyAddress',
      label: 'Address',
      fields: ['propertyAddress'],
      summary: (v) => text(v.propertyAddress),
    },
    { id: 'city', label: 'City', fields: ['city'], summary: (v) => text(v.city) },
    { id: 'state', label: 'State', fields: ['state'], summary: (v) => text(v.state) },
    { id: 'zip', label: 'Zip Code', fields: ['zip'], summary: (v) => text(v.zip) },
    {
      id: 'jobNumber',
      label: 'Job Number',
      fields: ['jobNumber'],
      summary: (v) => text(v.jobNumber),
    },
    {
      id: 'poNumber',
      label: 'Tracking Number',
      fields: ['poNumber'],
      summary: (v) => text(v.poNumber),
    },
    { id: 'name', label: 'Brief Description', fields: ['name'], summary: (v) => text(v.name) },
    {
      id: 'servicesNeeded',
      label: 'Services Needed',
      fields: ['servicesNeeded'],
      summary: (v) => text(v.servicesNeeded),
    },
    { id: 'logNotes', label: 'Notes', fields: ['logNotes'], summary: (v) => text(v.logNotes) },
  ];

  const panels = JOB_SEARCH_SECTIONS.map((section) => ({
    id: section.id,
    label: section.label,
    // "Any", or how many of this panel's filters are set.
    summary: (values: UnifiedJobSearchScheme) => {
      const set = dimensions.filter(
        (dimension) =>
          dimension.fields.some((field) => (section.fields as readonly string[]).includes(field)) &&
          dimension.fields.some(
            (field) =>
              JSON.stringify(values[field]) !==
              JSON.stringify(defaultUnifiedJobSearchValues[field]),
          ),
      );
      if (set.length === 0) return 'Any';
      return set.length === 1 ? set[0].label : `${set.length} set`;
    },
    render: () => <UnifiedJobSearchForm section={section.id} />,
  }));

  const openJob = (result: UnifiedJobSearchResult) => {
    const route = getResultRoute(result);
    if (route.to === '/jobs/$jobId') {
      // The job record's Back names this search and returns to it with the filters.
      void navigate({
        to: route.to,
        params: route.params,
        search: { returnTo: currentSearchHref() },
      });
      return;
    }
    void navigate({ to: route.to, params: route.params as never });
  };

  return (
    <PhoneSearchLayout
      form={form}
      searchField="globalSearch"
      // The desktop text ("Search jobs, customers, addresses... or job #") doesn't fit a phone's
      // box; this names the same reach in the space there is.
      searchPlaceholder="Search job #, name or address"
      defaultFilters={defaultUnifiedJobSearchValues}
      dimensions={dimensions}
      sheetTitle="Filter jobs"
      panels={panels}
      sortOptions={sortOptions}
      sort={sort}
      defaultSort={{ id: 'date_started', desc: true }}
      onSortChange={onSortChange}
    >
      {({ isFiltered, clearFilters }) => (
        <ListResultsPhone
          itemLabel="jobs"
          emptyTitle="No jobs found"
          rowCount={pages.rows.length}
          totalCount={pages.totalCount}
          isPending={pages.isPending}
          isFetching={pages.isFetching}
          isError={pages.isError}
          isFetchingNextPage={pages.isFetchingNextPage}
          hasNextPage={pages.hasNextPage}
          onLoadMore={() => void pages.fetchNextPage()}
          onRetry={() => void pages.refetch()}
          isFiltered={isFiltered}
          onClearFilters={clearFilters}
        >
          {pages.rows.map((result) => {
            const customerName = text(result.customerName);
            const businessName = text(result.businessName);
            const title = customerName || businessName || 'Unknown Customer';
            const jobNumber = result.jobNumber || String(result.eventId);
            const department = result.departmentId
              ? lookups.departmentName(result.departmentId)
              : undefined;
            return (
              <SearchResultRow
                key={result.eventId}
                title={title}
                lines={[
                  customerName && businessName ? businessName : '',
                  `Job #${jobNumber} · ${result.dateStarted ? formatDate(result.dateStarted) : '-'}`,
                  <Fragment key="status">
                    <RowPill tone={jobStatusTone(result.jobStatus)}>
                      {result.jobStatus || 'Unknown'}
                    </RowPill>
                    {[isResultProject(result) ? 'Project' : 'Service Call', department]
                      .filter(Boolean)
                      .join(' · ')}
                  </Fragment>,
                  [result.propertyAddress, result.propertyCity]
                    .map(text)
                    .filter(Boolean)
                    .join(', '),
                ]}
                actionLabel={`${onSelectResult ? 'Select' : 'Open'} job ${jobNumber}`}
                hint={onSelectResult ? 'Select' : undefined}
                onSelect={() => (onSelectResult ? onSelectResult(result) : openJob(result))}
                corner={
                  onSelectResult ? undefined : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`More actions for job ${jobNumber}`}
                        >
                          More
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {result.customerId > 0 && (
                          <DropdownMenuItem asChild>
                            <Link
                              to="/customers/$customerId"
                              params={{ customerId: result.customerId }}
                            >
                              View Customer
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {result.propertyId > 0 && (
                          <DropdownMenuItem asChild>
                            <Link
                              to="/properties/$propertyId"
                              params={{ propertyId: result.propertyId }}
                            >
                              View Property
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {canViewCostReport && !isResultProject(result) && (
                          <DropdownMenuItem asChild>
                            <Link
                              to="/cost-report/$jobNumber"
                              params={{ jobNumber: result.eventId }}
                            >
                              View Cost Report
                            </Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link
                            to={getResultRoute(result).to}
                            params={getResultRoute(result).params as never}
                            target="_blank"
                          >
                            Open in New Tab
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )
                }
              />
            );
          })}
        </ListResultsPhone>
      )}
    </PhoneSearchLayout>
  );
};
